import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { Button } from "../components/ui/button";
import { ArrowLeft, ShoppingBag, Check, CaretLeft, CaretRight } from "@phosphor-icons/react";
import Footer from "../components/Footer";
import ShareButton from "../components/ShareButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [added, setAdded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const { addItem } = useCart();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    axios
      .get(`${API}/products/${id}`)
      .then((res) => {
        setProduct(res.data);
        setActiveIndex(0);
        return axios.get(`${API}/products?category=${res.data.category}`);
      })
      .then((res) => {
        setRelated(res.data.filter((p) => p.id !== id).slice(0, 4));
      })
      .catch(console.error);
    setAdded(false);
  }, [id]);

  const handleAdd = () => {
    addItem(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  // Build gallery images array: use product.images if available, else fallback to [product.image]
  const galleryImages = product
    ? product.images && product.images.length > 0
      ? product.images
      : [product.image]
    : [];

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % galleryImages.length);
  }, [galleryImages.length]);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  }, [galleryImages.length]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="w-6 h-6 border-2 border-[#E01A23] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const hasMultiple = galleryImages.length > 1;

  return (
    <div data-testid="product-detail-page" className="pt-20 md:pt-24 min-h-screen">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Link
          to="/shop"
          data-testid="back-to-shop"
          className="inline-flex items-center gap-2 text-sm text-[#3a3a3a] hover:text-[#E01A23] transition-colors mt-8 mb-8"
          style={{ fontFamily: "'Courier New', Courier, monospace" }}
        >
          <ArrowLeft size={14} />
          back to shop
        </Link>

        <div className="grid md:grid-cols-2 gap-10 md:gap-16">
          {/* ─── Image Gallery ─── */}
          <div data-testid="product-gallery">
            {/* Main Image */}
            <div className="relative overflow-hidden rounded-2xl aspect-square bg-[#EBD988] group">
              <img
                src={galleryImages[activeIndex]}
                alt={`${product.name} — image ${activeIndex + 1}`}
                data-testid="product-image"
                className="w-full h-full object-cover transition-opacity duration-300"
                key={activeIndex}
              />

              {/* Nav arrows */}
              {hasMultiple && (
                <>
                  <button
                    data-testid="gallery-prev"
                    onClick={goPrev}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  >
                    <CaretLeft size={18} weight="bold" className="text-[#1A1A1A]" />
                  </button>
                  <button
                    data-testid="gallery-next"
                    onClick={goNext}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  >
                    <CaretRight size={18} weight="bold" className="text-[#1A1A1A]" />
                  </button>

                  {/* Dot indicators */}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {galleryImages.map((_, idx) => (
                      <button
                        key={idx}
                        data-testid={`gallery-dot-${idx}`}
                        onClick={() => setActiveIndex(idx)}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          idx === activeIndex
                            ? "bg-[#E01A23] w-4"
                            : "bg-white/60 hover:bg-white/90"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnail Strip */}
            {hasMultiple && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1" data-testid="thumbnail-strip">
                {galleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    data-testid={`thumbnail-${idx}`}
                    onClick={() => setActiveIndex(idx)}
                    className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden transition-all duration-300 ${
                      idx === activeIndex
                        ? "ring-2 ring-[#E01A23] ring-offset-2 ring-offset-[#F5E6A0]"
                        : "opacity-50 hover:opacity-80"
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ─── Product Info ─── */}
          <div className="flex flex-col justify-center">
            <p
              className="text-xs tracking-widest text-[#E01A23] mb-2"
              style={{ fontFamily: "'Courier New', Courier, monospace" }}
            >
              {product.category}
            </p>
            <h1
              className="text-3xl sm:text-4xl md:text-5xl tracking-tighter font-light text-[#1A1A1A] mb-4"
              style={{ fontFamily: "'Lazy Dog', cursive" }}
              data-testid="product-name"
            >
              {product.name}
            </h1>
            <p
              className="text-2xl text-[#1A1A1A] mb-6"
              style={{ fontFamily: "'Lazy Dog', cursive" }}
              data-testid="product-price"
            >
              {formatPrice(product.price)}
            </p>
            <p
              className="text-base text-[#3a3a3a] leading-relaxed mb-8"
              style={{ fontFamily: "'Courier New', Courier, monospace" }}
              data-testid="product-description"
            >
              {product.description}
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                data-testid="add-to-cart-button"
                onClick={handleAdd}
                className={`h-12 px-10 text-sm tracking-wider rounded-full transition-all duration-300 w-fit ${
                  added
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-[#E01A23] hover:bg-[#c4161e]"
                } text-white`}
                style={{ fontFamily: "'Courier New', Courier, monospace" }}
              >
                {added ? (
                  <>
                    <Check size={16} weight="bold" className="mr-2" />
                    added!
                  </>
                ) : (
                  <>
                    <ShoppingBag size={16} weight="bold" className="mr-2" />
                    add to cart
                  </>
                )}
              </Button>

              <ShareButton
                url={typeof window !== "undefined" ? window.location.href : ""}
                title={`${product.name} — vandy's`}
                description={product.description}
                image={product.image}
              />
            </div>

            <div className="mt-8 border-t border-[#E01A23]/10 pt-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[#555] tracking-wider mb-1" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
                    availability
                  </p>
                  <p className="text-sm text-[#1A1A1A]" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
                    {product.in_stock ? "in stock" : "sold out"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#555] tracking-wider mb-1" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
                    images
                  </p>
                  <p className="text-sm text-[#1A1A1A]" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
                    {galleryImages.length} photo{galleryImages.length > 1 ? "s" : ""}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="py-20 md:py-28">
            <h2
              className="text-3xl md:text-4xl tracking-tight font-normal text-[#1A1A1A] mb-10"
              style={{ fontFamily: "'Lazy Dog', cursive" }}
            >
              you may also like
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8">
              {related.map((p) => (
                <div key={p.id} className="group" data-testid={`related-product-${p.id}`}>
                  <Link to={`/product/${p.id}`}>
                    <div className="overflow-hidden rounded-xl aspect-square bg-[#EBD988]">
                      <img
                        src={p.image}
                        alt={p.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  </Link>
                  <div className="mt-3">
                    <Link to={`/product/${p.id}`}>
                      <h3
                        className="text-sm text-[#1A1A1A] hover:text-[#E01A23] transition-colors"
                        style={{ fontFamily: "'Courier New', Courier, monospace" }}
                      >
                        {p.name}
                      </h3>
                    </Link>
                    <p className="text-sm text-[#3a3a3a] mt-0.5" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
                      {formatPrice(p.price)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
